import { MaterialCommunityIcons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { StyleSheet, View } from "react-native";
import { Text } from "react-native-paper";

import { courseTheme } from "@/lib/training/courseTheme";
import type { Screen } from "@/lib/training/types";

type Cover = Extract<Screen, { kind: "cover" }>;

// Welcome / cover screen. The roadmap here previews what this course covers;
// which COURSES in the bundle are finished is server state and lives on the
// training list, so nothing on this screen reads local completion.

export function CoverScreen({ screen }: { screen: Cover }) {
    const roadmap = screen.roadmap ?? [];
    const firstUpcoming = roadmap.find((r) => r.now)?.n ?? roadmap[0]?.n;

    return (
        <View>
            <LinearGradient
                colors={[courseTheme.navy, courseTheme.navySoft, courseTheme.tealD]}
                start={{ x: 0.1, y: 0 }}
                end={{ x: 0.9, y: 1 }}
                style={styles.cover}
            >
                <View style={styles.ribbon}>
                    <Text style={styles.ribbonText}>{screen.ribbon ?? ""}</Text>
                </View>
                <Text style={styles.heading}>{screen.heading}</Text>
                <Text style={styles.sub}>{screen.sub ?? ""}</Text>
                <View style={styles.stats}>
                    {(screen.stats ?? []).map((s, i) => (
                        <View key={i} style={styles.cstat}>
                            <Text style={styles.cstatNum}>{s.n}</Text>
                            <Text style={styles.cstatLabel}>{s.label}</Text>
                        </View>
                    ))}
                </View>
            </LinearGradient>

            {/*  Only when there is something to list. `roadmap` is not in the
                bundle schema and production content has never carried it, so
                this heading rendered over an empty space on every cover screen
                — the nurse was told what she'd cover and then shown nothing. */}
            {roadmap.length > 0 && (
                <View style={styles.roadH}>
                    <Text style={styles.roadHText}>What you'll cover</Text>
                </View>
            )}
            <View style={styles.road}>
                {roadmap.map((r) => {
                    const done = false;
                    const isNow = r.n === firstUpcoming;
                    return (
                        <View
                            key={r.n}
                            style={[
                                styles.roadItem,
                                isNow && styles.roadItemNow,
                                done && styles.roadItemDone
                            ]}
                        >
                            <View
                                style={[
                                    styles.roadN,
                                    isNow && styles.roadNNow,
                                    done && styles.roadNDone
                                ]}
                            >
                                {done ? (
                                    <MaterialCommunityIcons
                                        name="check"
                                        size={16}
                                        color="#FFFFFF"
                                    />
                                ) : (
                                    <Text style={[styles.roadNText, isNow && styles.roadNTextNow]}>
                                        {r.n}
                                    </Text>
                                )}
                            </View>
                            <Text style={styles.roadT}>{r.title}</Text>
                            {done ? (
                                <Text style={styles.roadDone}>Done</Text>
                            ) : isNow ? (
                                <View style={styles.nowTag}>
                                    <Text style={styles.nowTagText}>Start</Text>
                                </View>
                            ) : (
                                <Text style={styles.roadMin}>{r.min}</Text>
                            )}
                        </View>
                    );
                })}
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    cover: { borderRadius: 26, padding: 24, marginTop: 6, overflow: "hidden" },
    ribbon: {
        alignSelf: "flex-start",
        backgroundColor: "rgba(255,255,255,0.12)",
        borderRadius: 99,
        paddingHorizontal: 12,
        paddingVertical: 6,
        marginBottom: 14
    },
    ribbonText: { fontSize: 11, fontWeight: "700", color: "#CFE6E0", letterSpacing: 0.3 },
    heading: { fontSize: 26, fontWeight: "800", color: "#FFFFFF", lineHeight: 31 },
    sub: { fontSize: 14, lineHeight: 20, color: "#C6D6E2", marginTop: 8 },
    ecg: { marginTop: 16, opacity: 0.9 },
    stats: { flexDirection: "row", gap: 10, marginTop: 18 },
    cstat: {
        flex: 1,
        backgroundColor: "rgba(255,255,255,0.08)",
        borderRadius: 14,
        paddingVertical: 12,
        alignItems: "center"
    },
    cstatNum: { fontSize: 19, fontWeight: "800", color: "#FFFFFF" },
    cstatLabel: { fontSize: 11, color: "#AFC4D2", marginTop: 2, textAlign: "center" },
    roadH: { marginTop: 22, marginBottom: 10 },
    roadHText: {
        fontSize: 12,
        fontWeight: "700",
        textTransform: "uppercase",
        letterSpacing: 0.4,
        color: courseTheme.slateL
    },
    road: { gap: 8 },
    roadItem: {
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
        borderWidth: 1,
        borderColor: courseTheme.line,
        borderRadius: 14,
        padding: 12,
        backgroundColor: courseTheme.white
    },
    roadItemNow: { borderColor: courseTheme.tealTint2, backgroundColor: courseTheme.tealTint },
    roadItemDone: { borderColor: "#BFE8CD", backgroundColor: courseTheme.greenTint },
    roadN: {
        width: 26,
        height: 26,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: courseTheme.line,
        backgroundColor: courseTheme.paper,
        alignItems: "center",
        justifyContent: "center"
    },
    roadNNow: { backgroundColor: courseTheme.teal, borderColor: courseTheme.teal },
    roadNDone: { backgroundColor: courseTheme.green, borderColor: courseTheme.green },
    roadNText: { fontSize: 13, fontWeight: "800", color: courseTheme.slate },
    roadNTextNow: { color: "#FFFFFF" },
    roadT: { flex: 1, fontSize: 14.5, fontWeight: "700", color: courseTheme.navy },
    roadMin: { fontSize: 12, color: courseTheme.slateL, fontWeight: "600" },
    roadDone: { fontSize: 12, fontWeight: "800", color: courseTheme.green },
    nowTag: {
        backgroundColor: courseTheme.teal,
        borderRadius: 99,
        paddingHorizontal: 10,
        paddingVertical: 4
    },
    nowTagText: { fontSize: 11, fontWeight: "800", color: "#FFFFFF" }
});
